import { z } from 'zod';
import { resourceKeySchema, timestampSchema, versionedSchema } from './common';
import { viewVisibilitySchema } from './views';
import { mapDefaultViewSchema, mapLayerKeySchema, MAX_MAP_LAYERS } from './mapping';

/**
 * A saved map: what is on it, what is filtered out of it, and where it opens.
 *
 * One project's features answer several audiences, and each of them wants a
 * different subset. "Site induction" is the compound, its access routes and its
 * hazards, framed on the gate. "This week's possessions" is work areas only,
 * narrowed to the ones in progress, framed on the corridor. Both are the same
 * data; neither is reachable today without a reader rebuilding it by hand every
 * time, from memory, and getting a slightly different answer.
 *
 * The shape `DashboardView` established -- a slug, a title, a visibility, an
 * owner, and a versioned definition document -- because it is the same problem:
 * a saved arrangement somebody made and may want to share.
 *
 * ## What a view does not carry: style
 *
 * Deliberately, and it is the decision most worth arguing about.
 *
 * A style is a property of the *layer*: it is the answer to "what does a hazard
 * look like here", and the whole point of the layer catalog is that there is one
 * answer. A view that could override it would mean the same layer drawn two ways
 * in two views, so a colour would stop meaning anything until the reader checked
 * which view they were in -- and it would fork the style editor, which would have
 * to ask whether it was editing the layer or this view's opinion of it.
 *
 * It also costs correctness. The collection endpoint computes its projection
 * from the catalog's styles; a view whose override named a field the catalog did
 * not would compile against a projection that never carried it, and draw its
 * fallback. Making that right means the features request naming the view, which
 * is a second way for the client and the server to disagree about what is being
 * drawn.
 *
 * When somebody does ask for it -- and they will -- the shape is: overrides on
 * the definition below, `?view=` on the features endpoint so the projection
 * follows, and a style editor that says which of the two it is writing to.
 */

export const MAP_VIEW_SCHEMA_VERSION = 1;

/** How many classes of one layer a view may hide. Generous: a categorical style
 *  is capped at twelve stops plus its fallback, so this is "all of them, twice". */
const MAX_HIDDEN_CLASSES = 26;

/**
 * The classes filtered out, by layer key.
 *
 * `null` is a member of the value list rather than an absence, and it means the
 * fallback class -- "everything the stops do not name, including features with
 * no value at all". It has to be expressible: hiding the unclassified features
 * is one of the two things anybody does with these filters, and a list that
 * could not say so would silently drop the instruction.
 */
export const mapViewHiddenClassesSchema = z.record(
  mapLayerKeySchema,
  z.array(z.string().max(120).nullable()).max(MAX_HIDDEN_CLASSES),
);
export type MapViewHiddenClasses = z.infer<typeof mapViewHiddenClassesSchema>;

export const mapViewDefinitionSchema = z
  .object({
    schemaVersion: z.literal(MAP_VIEW_SCHEMA_VERSION),
    /**
     * The layers drawn, by key. Empty means every layer -- the same collapse the
     * live map makes, and for the same reason: a view saved when everything was
     * on should keep showing everything after somebody adds a sixth layer, not
     * freeze the catalog as it stood the day it was saved.
     */
    layers: z.array(mapLayerKeySchema).max(MAX_MAP_LAYERS),
    hiddenClasses: mapViewHiddenClassesSchema,
    /**
     * Where the map opens, or null to fit to whatever the view shows.
     *
     * Null is the better default and is what "save" offers first: a fitted
     * camera stays correct when the project's footprint changes, where a stored
     * one is a decision about a moment. A view of one compound wants the stored
     * camera; a view of the whole corridor almost never does.
     */
    camera: mapDefaultViewSchema.nullable(),
  })
  .strict();
export type MapViewDefinition = z.infer<typeof mapViewDefinitionSchema>;

export const mapViewSchema = z
  .object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    slug: resourceKeySchema,
    name: z.string(),
    visibility: viewVisibilitySchema,
    ownerUserId: z.string().uuid(),
    /** Whether the reader may edit it. Sent rather than derived in the browser,
     *  which would have to know how ownership and permissions combine. */
    isOwner: z.boolean(),
    /** Who to ask about a shared view somebody else made. Null when the account
     *  is gone. */
    ownerName: z.string().nullable(),
    definition: mapViewDefinitionSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .merge(versionedSchema);
export type MapView = z.infer<typeof mapViewSchema>;

export const createMapViewSchema = z
  .object({
    slug: resourceKeySchema,
    name: z.string().min(1).max(120),
    visibility: viewVisibilitySchema.default('private'),
    definition: mapViewDefinitionSchema,
  })
  .strict();
export type CreateMapView = z.infer<typeof createMapViewSchema>;

/**
 * `slug` is absent, as it is on a layer and for the same reason: it is what a
 * shared link carries, and renaming it would break every link somebody has sent.
 */
export const updateMapViewSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    visibility: viewVisibilitySchema.optional(),
    definition: mapViewDefinitionSchema.optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateMapView = z.infer<typeof updateMapViewSchema>;

/** How many saved views one project may hold. A list somebody has to scroll is
 *  a list they will stop reading, and the point of a view is to be found. */
export const MAX_MAP_VIEWS = 50;

export function emptyMapViewDefinition(): MapViewDefinition {
  return {
    schemaVersion: MAP_VIEW_SCHEMA_VERSION,
    layers: [],
    hiddenClasses: {},
    camera: null,
  };
}
